import { useCallback, useEffect, useMemo, useState } from "react";
import type React from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { api } from "../../api/client";
import { theme, card, btnPrimary, btnSecondary, countTag, emptyFrame, floatingSheet } from "../../theme";
import { StatusTag } from "../../components/Drawing";
import { useConsultantShell } from "./ConsultantShellContext";
import { ScopeToggle } from "./ScopeToggle";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";

// ── Consultant calendar ───────────────────────────────────────────────────
// Month, team-week and agenda views over four sources:
//   GET /consultant/schedule?from&to  every client's schedule events
//   GET /consultant/slots?month       my confirmed consultancy bookings
//   GET /consultant/slots/pending     booking requests made to me
//   GET /consultant/team-calendar     every consultant's booked and requested hours, blocked days
//                                     and the firm's holidays (firm admins set them from the day panel)
// A day is opened in the side panel, which is where a day is blocked, a booking
// answered and a conflict resolved. Capacity rules live on the server
// (backend/src/lib/consultantCapacity.ts); this page only displays them.

export interface ConsultantEvent {
  id: string;
  organizationId: string;
  organizationName: string | null;
  title: string;
  eventDate: string;
  kind: string;
  notes: string | null;
  completedAt: string | null;
}

interface SlotEntry {
  id: string;
  organizationId: string;
  organizationName: string;
  grade: string;
  slotDate: string;
  hours: number;
  note: string | null;
  costPence: number;
  status?: string;
  assignedUserName?: string | null;
}

interface BlockedDay { id: string; date: string; reason: string | null }
interface Holiday { date: string; reason: string | null }

interface TeamConsultant {
  id: string;
  displayName: string;
  primaryGrade: string | null;
  bookings: Record<string, number>;   // dateKey -> confirmed hours
  pending?: Record<string, number>;   // dateKey -> hours held by open requests
  blockedDays: string[];              // dateKey[]
}

type View = "month" | "week" | "agenda";
type KindFilter = "all" | "review" | "audit" | "renewal" | "meeting" | "booking";

const DAY_HOURS = 8;
const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const VIEW_KEY = "consultant.calendar.view";

/** Event kinds are meaning, so they take the theme's semantic tokens (light, dark and brand schemes alike). */
export const KIND_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  review:  { bg: theme.color.accentBg,     color: theme.color.accentText,  label: "Review" },
  audit:   { bg: theme.color.auditorBg,    color: theme.color.auditor,     label: "Audit" },
  renewal: { bg: theme.color.successBg,    color: theme.color.successText, label: "Renewal" },
  meeting: { bg: theme.color.warningBg,    color: theme.color.warningText, label: "Meeting" },
  booking: { bg: theme.color.consultantBg, color: theme.color.consultant,  label: "Booking" },
};

// ── Date helpers (local calendar days, keyed YYYY-MM-DD) ──────────────────

function keyOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function fromKey(k: string): Date {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
}
function addDays(k: string, n: number): string {
  const d = fromKey(k);
  d.setDate(d.getDate() + n);
  return keyOf(d);
}
function mondayOf(k: string): string {
  const d = fromKey(k);
  return addDays(k, -((d.getDay() + 6) % 7));
}
function monthKeysBetween(from: string, to: string): string[] {
  const out: string[] = [];
  let y = Number(from.slice(0, 4)), m = Number(from.slice(5, 7));
  const endY = Number(to.slice(0, 4)), endM = Number(to.slice(5, 7));
  while (y < endY || (y === endY && m <= endM)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return out;
}
const isDateKey = (v: string | null): v is string => !!v && /^\d{4}-\d{2}-\d{2}$/.test(v);
/** Consultancy time is weekdays only (backend lib/consultantCapacity.ts). */
const isWeekendKey = (k: string): boolean => { const d = fromKey(k).getDay(); return d === 0 || d === 6; };
const hrs = (h: number) => (h >= DAY_HOURS && h % DAY_HOURS === 0 ? `${h / DAY_HOURS}d` : `${h}h`);
const longDate = (k: string) => fromKey(k).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const errText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

function readView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    if (v === "month" || v === "week" || v === "agenda") return v;
  } catch { /* storage unavailable */ }
  return "month";
}

// ── Day content ───────────────────────────────────────────────────────────

interface DayItems {
  events: ConsultantEvent[];
  slots: SlotEntry[];      // my confirmed bookings
  pending: SlotEntry[];    // requests made to me
}

function eventState(e: ConsultantEvent, today: string): "done" | "overdue" | "open" {
  if (e.completedAt) return "done";
  return e.eventDate.slice(0, 10) < today ? "overdue" : "open";
}

function Chip({ label, sub, style, dashed, done, overdue, title, onClick }: {
  label: string; sub?: string; style: { bg: string; color: string }; dashed?: boolean; done?: boolean; overdue?: boolean; title?: string; onClick?: () => void;
}) {
  return (
    <div
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      title={title}
      style={{
        fontSize: 10.5, lineHeight: 1.35, padding: "2px 5px", borderRadius: 4, marginBottom: 2,
        background: dashed ? "transparent" : style.bg, color: style.color,
        border: dashed ? `1px dashed ${style.color}` : "1px solid transparent",
        borderLeft: overdue ? `3px solid ${theme.color.danger}` : dashed ? `1px dashed ${style.color}` : "1px solid transparent",
        overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", minWidth: 0,
        textDecoration: done ? "line-through" : "none", opacity: done ? 0.55 : 1,
        cursor: onClick ? "pointer" : "inherit",
      }}
    >
      {sub && <span style={{ fontWeight: 700 }}>{sub} · </span>}{label}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────

export function CalendarTab({ onSelect }: { onSelect: (id: string, name: string) => void }) {
  const { user, scope, isFirmAdmin } = useConsultantShell();
  const [params, setParams] = useSearchParams();
  const paramDate = params.get("date");
  const today = keyOf(new Date());

  const [view, setViewState] = useState<View>(readView);
  const [anchor, setAnchor] = useState<string>(isDateKey(paramDate) ? paramDate : today);
  const [selected, setSelected] = useState<string | null>(isDateKey(paramDate) ? paramDate : null);
  const [kindFilter, setKindFilter] = useState<KindFilter>("all");
  const [clientFilter, setClientFilter] = useState("");

  const [events, setEvents] = useState<ConsultantEvent[] | null>(null);
  const [slots, setSlots] = useState<SlotEntry[]>([]);
  const [pending, setPending] = useState<SlotEntry[]>([]);
  const [blocked, setBlocked] = useState<BlockedDay[]>([]);
  const [team, setTeam] = useState<TeamConsultant[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const [resolving, setResolving] = useState<string | null>(null); // date whose conflicts are open
  // Firm holidays (ConsultancyBlockedDay): nobody at the firm is bookable. Set by firm admins.
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  // Bookings (any consultant's) already on a day just made a firm holiday.
  const [holidayConflicts, setHolidayConflicts] = useState<{ date: string; slots: SlotEntry[] } | null>(null);

  // Follow ?date= when a Today item or a link opens the calendar on a day.
  useEffect(() => {
    if (isDateKey(paramDate)) { setAnchor(paramDate); setSelected(paramDate); }
  }, [paramDate]);

  const setView = (v: View) => {
    setViewState(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage unavailable */ }
  };

  const selectDay = (k: string | null) => {
    setSelected(k);
    setActionError(null);
    const next = new URLSearchParams(params);
    if (k) next.set("date", k); else next.delete("date");
    setParams(next, { replace: true });
  };

  // The visible range, padded to whole weeks so the grid never ends in a hole.
  const range = useMemo(() => {
    if (view === "week") { const from = mondayOf(anchor); return { from, to: addDays(from, 6) }; }
    if (view === "agenda") return { from: anchor, to: addDays(anchor, 41) };
    const first = `${anchor.slice(0, 7)}-01`;
    const last = keyOf(new Date(Number(anchor.slice(0, 4)), Number(anchor.slice(5, 7)), 0));
    return { from: mondayOf(first), to: addDays(mondayOf(last), 6) };
  }, [view, anchor]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    const months = monthKeysBetween(range.from, range.to);
    Promise.all([
      api.get<{ events: ConsultantEvent[] }>("/consultant/schedule", { from: range.from, to: range.to, scope }),
      Promise.all(months.map((m) => api.get<{ slots: SlotEntry[] }>("/consultant/slots", { month: m }))),
      api.get<{ slots: SlotEntry[] }>("/consultant/slots/pending"),
      api.get<{ blockedDays: BlockedDay[] }>("/consultant/blocked-days"),
      Promise.all(months.map((m) => api.get<{ consultants: TeamConsultant[]; holidays?: Holiday[] }>("/consultant/team-calendar", { month: m }))),
    ])
      .then(([ev, slotPages, pend, blk, teamPages]) => {
        if (cancelled) return;
        setEvents(ev.events);
        setSlots(slotPages.flatMap((p) => p.slots));
        setPending(pend.slots);
        setBlocked(blk.blockedDays);
        setHolidays(teamPages.flatMap((p) => p.holidays ?? []));
        // Merge each consultant's months into one record.
        const merged = new Map<string, TeamConsultant>();
        for (const page of teamPages) {
          for (const c of page.consultants) {
            const prev = merged.get(c.id);
            merged.set(c.id, prev
              ? { ...prev, bookings: { ...prev.bookings, ...c.bookings }, pending: { ...prev.pending, ...c.pending }, blockedDays: [...prev.blockedDays, ...c.blockedDays] }
              : c);
          }
        }
        setTeam([...merged.values()]);
      })
      .catch((e) => { if (!cancelled) { setLoadError(errText(e, "Could not load the calendar")); setEvents((prev) => prev ?? []); } });
    return () => { cancelled = true; };
  }, [range.from, range.to, reload, scope]);

  const refresh = useCallback(() => setReload((n) => n + 1), []);

  // ── Derived ──────────────────────────────────────────────────────────────
  const blockedSet = useMemo(() => new Set(blocked.map((b) => b.date.slice(0, 10))), [blocked]);
  const blockedReason = useMemo(() => new Map(blocked.map((b) => [b.date.slice(0, 10), b.reason])), [blocked]);
  const holidayMap = useMemo(() => new Map(holidays.map((h) => [h.date, h.reason])), [holidays]);
  // ?slot=<id> (Today links a booking request here, with ?date=): bring its request row into view.
  useDeepLinkScroll(params.get("slot"), pending.length > 0);

  const clients = useMemo(() => {
    const m = new Map<string, string>();
    for (const e of events ?? []) if (e.organizationName) m.set(e.organizationId, e.organizationName);
    for (const s of [...slots, ...pending]) m.set(s.organizationId, s.organizationName);
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [events, slots, pending]);

  const byDay = useMemo(() => {
    const map: Record<string, DayItems> = {};
    const at = (k: string) => (map[k] ??= { events: [], slots: [], pending: [] });
    const clientOk = (orgId: string) => !clientFilter || orgId === clientFilter;
    const showEvents = kindFilter !== "booking";
    const showBookings = kindFilter === "all" || kindFilter === "booking";
    if (showEvents) {
      for (const e of events ?? []) {
        if (kindFilter !== "all" && e.kind !== kindFilter) continue;
        if (!clientOk(e.organizationId)) continue;
        at(e.eventDate.slice(0, 10)).events.push(e);
      }
    }
    if (showBookings) {
      for (const s of slots) if (clientOk(s.organizationId)) at(s.slotDate.slice(0, 10)).slots.push(s);
      for (const s of pending) if (clientOk(s.organizationId)) at(s.slotDate.slice(0, 10)).pending.push(s);
    }
    return map;
  }, [events, slots, pending, kindFilter, clientFilter]);

  const myBooked = useCallback((k: string) => slots.filter((s) => s.slotDate.slice(0, 10) === k).reduce((n, s) => n + s.hours, 0), [slots]);
  const others = team.filter((c) => c.id !== user.id);

  // ── Actions ──────────────────────────────────────────────────────────────
  async function run(key: string, fn: () => Promise<unknown>, fallback: string) {
    setBusy(key);
    setActionError(null);
    try { await fn(); refresh(); }
    catch (e) { setActionError(errText(e, fallback)); }
    finally { setBusy(null); }
  }

  async function toggleBlock(k: string) {
    if (blockedSet.has(k)) {
      await run(`block:${k}`, () => api.del(`/consultant/blocked-days/${k}`), "Could not unblock the day");
      return;
    }
    setBusy(`block:${k}`);
    setActionError(null);
    try {
      const res = await api.post<{ conflicts?: unknown[] }>("/consultant/blocked-days", { date: k });
      if (res.conflicts && res.conflicts.length > 0) setResolving(k);
      refresh();
    } catch (e) { setActionError(errText(e, "Could not block the day")); }
    finally { setBusy(null); }
  }

  async function toggleHoliday(k: string, reason: string) {
    if (holidayMap.has(k)) {
      await run(`holiday:${k}`, () => api.del(`/consultant/workspace/holidays/${k}`), "Could not remove the firm holiday");
      return;
    }
    setBusy(`holiday:${k}`);
    setActionError(null);
    try {
      const res = await api.post<{ conflicts: Array<{ id: string; organizationId: string; organizationName: string; grade: string; hours: number; status: string; consultantName: string | null }> }>(
        "/consultant/workspace/holidays", { date: k, reason: reason.trim() || undefined });
      if (res.conflicts.length > 0) {
        setHolidayConflicts({
          date: k,
          slots: res.conflicts.map((c) => ({ id: c.id, organizationId: c.organizationId, organizationName: `${c.organizationName}${c.consultantName ? ` · ${c.consultantName}` : ""}`, grade: c.grade, slotDate: k, hours: c.hours, note: null, costPence: 0, status: c.status })),
        });
      }
      refresh();
    } catch (e) { setActionError(errText(e, "Could not set the firm holiday")); }
    finally { setBusy(null); }
  }

  const answer = (id: string, action: "confirmed" | "declined") =>
    run(`slot:${id}`, () => api.patch(`/consultant/slots/${id}`, { action }), "Could not update the request");

  // ── Navigation ───────────────────────────────────────────────────────────
  const step = (dir: -1 | 1) => {
    if (view === "week") setAnchor(addDays(anchor, 7 * dir));
    else if (view === "agenda") setAnchor(addDays(anchor, 42 * dir));
    else {
      const d = fromKey(`${anchor.slice(0, 7)}-01`);
      d.setMonth(d.getMonth() + dir);
      setAnchor(keyOf(d));
    }
  };
  const title = view === "month"
    ? fromKey(`${anchor.slice(0, 7)}-01`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })
    : `${fromKey(range.from).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${fromKey(range.to).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  const showsToday = today >= range.from && today <= range.to;

  if (events === null && !loadError) return <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>;

  // ── Render ───────────────────────────────────────────────────────────────
  const navBtn: React.CSSProperties = { background: "none", border: `1px solid ${theme.color.borderStrong}`, borderRadius: 3, padding: "4px 8px", cursor: "pointer", color: theme.color.muted, display: "flex", alignItems: "center" };
  const pill = (active: boolean, c?: { bg: string; color: string }): React.CSSProperties => ({
    padding: "5px 11px", borderRadius: 3, fontSize: 11.5, fontWeight: 600, cursor: "pointer", fontFamily: theme.font,
    border: `1px solid ${active ? (c?.color ?? theme.color.accent) : theme.color.border}`,
    background: active ? (c?.bg ?? theme.color.accentBg) : "transparent",
    color: active ? (c?.color ?? theme.color.accentText) : theme.color.subtle,
  });

  const days: string[] = [];
  for (let k = range.from; k <= range.to; k = addDays(k, 1)) days.push(k);
  const viewMonth = anchor.slice(0, 7);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button aria-label="Previous" onClick={() => step(-1)} style={navBtn}><ChevronLeft size={15} /></button>
          <span style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading, minWidth: 170, textAlign: "center" }}>{title}</span>
          <button aria-label="Next" onClick={() => step(1)} style={navBtn}><ChevronRight size={15} /></button>
          {!showsToday && <button onClick={() => setAnchor(today)} style={{ ...pill(false), padding: "4px 10px" }}>Today</button>}
          <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
            {(["month", "week", "agenda"] as View[]).map((v) => (
              <button key={v} onClick={() => setView(v)} style={pill(view === v)}>{v === "week" ? "Team week" : v[0].toUpperCase() + v.slice(1)}</button>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {(["all", "review", "audit", "renewal", "meeting", "booking"] as KindFilter[]).map((k) => (
            <button key={k} onClick={() => setKindFilter(k)} style={pill(kindFilter === k, KIND_STYLE[k])}>
              {k === "all" ? "All" : k === "booking" ? "Bookings" : KIND_STYLE[k].label}
            </button>
          ))}
          <select
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            aria-label="Filter by client"
            style={{ padding: "5px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12, maxWidth: 200 }}
          >
            <option value="">All clients</option>
            {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <ScopeToggle compact />
        </div>
      </div>

      {loadError && <div style={{ fontSize: 13, color: theme.color.dangerText }}>{loadError} <button onClick={refresh} style={{ ...pill(false), marginLeft: 6 }}>Retry</button></div>}

      {/* Pending requests — always visible, they need an answer */}
      {pending.length > 0 && (
        <div style={{ ...card, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>
            Booking requests waiting for you
            <span style={{ ...countTag, marginLeft: 8, display: "inline-block" }}>{pending.length}</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {pending.map((s) => (
              <PendingRow key={s.id} s={s} busy={busy === `slot:${s.id}`} blockedThatDay={blockedSet.has(s.slotDate.slice(0, 10))}
                past={s.slotDate.slice(0, 10) < today}
                onOpenDay={() => { const k = s.slotDate.slice(0, 10); setAnchor(k); selectDay(k); }}
                onAnswer={(a) => answer(s.id, a)} />
            ))}
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 560px", minWidth: 0 }}>
          {view === "month" && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                {DAYS_OF_WEEK.map((d, i) => (
                  <div key={d} style={{ padding: "8px 0", textAlign: "center", fontSize: 11, fontWeight: 700, color: theme.color.faint, borderBottom: `1px solid ${theme.color.line}`, background: i >= 5 ? theme.color.panelAlt : "transparent" }}>{d}</div>
                ))}
                {days.map((k, i) => {
                  const items = byDay[k] ?? { events: [], slots: [], pending: [] };
                  const inMonth = k.slice(0, 7) === viewMonth;
                  const isToday = k === today;
                  const isPast = k < today;
                  const holiday = holidayMap.has(k);
                  const isBlocked = blockedSet.has(k) || holiday;
                  const conflict = isBlocked && items.slots.length > 0;
                  const weekend = i % 7 >= 5;
                  const chips = [
                    ...items.pending.map((s) => ({ key: s.id, node: <Chip key={s.id} dashed style={KIND_STYLE.booking} sub={`Request ${hrs(s.hours)}`} label={s.organizationName} title={`Booking request — ${s.organizationName}, ${hrs(s.hours)} ${s.grade}`} /> })),
                    ...items.slots.map((s) => ({ key: s.id, node: <Chip key={s.id} style={KIND_STYLE.booking} sub={hrs(s.hours)} label={s.organizationName} title={`${s.grade} ${hrs(s.hours)} — ${s.organizationName}`} /> })),
                    ...items.events.map((e) => {
                      const st = eventState(e, today);
                      return { key: e.id, node: <Chip key={e.id} style={KIND_STYLE[e.kind] ?? KIND_STYLE.meeting} sub={e.organizationName ?? undefined} label={e.title}
                        done={st === "done"} overdue={st === "overdue"} title={`${e.title} — ${e.organizationName ?? ""}${st === "overdue" ? " (overdue)" : st === "done" ? " (done)" : ""}`} /> };
                    }),
                  ];
                  const shown = chips.slice(0, 3);
                  return (
                    <div
                      key={k}
                      role="button"
                      tabIndex={0}
                      aria-label={`${longDate(k)}${chips.length ? `, ${chips.length} item${chips.length !== 1 ? "s" : ""}` : ""}${isBlocked ? ", blocked" : ""}`}
                      onClick={() => selectDay(k)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectDay(k); } }}
                      style={{
                        minHeight: 96, padding: 4, minWidth: 0, cursor: "pointer", position: "relative",
                        borderRight: i % 7 < 6 ? `1px solid ${theme.color.border}` : "none",
                        borderBottom: i < days.length - 7 ? `1px solid ${theme.color.border}` : "none",
                        background: isBlocked
                          ? `repeating-linear-gradient(135deg, ${theme.color.dangerBg}, ${theme.color.dangerBg} 5px, transparent 5px, transparent 10px)`
                          : weekend || !inMonth ? theme.color.panelAlt : "transparent",
                        outline: selected === k ? `2px solid ${theme.color.accent}` : "none",
                        outlineOffset: -2,
                        opacity: inMonth ? (isPast ? 0.7 : 1) : 0.45,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
                        <span style={{
                          fontSize: 11.5, fontWeight: isToday ? 800 : 500, minWidth: 20, height: 20, lineHeight: "20px", textAlign: "center", borderRadius: 3,
                          background: isToday ? theme.color.accent : "transparent", color: isToday ? "#fff" : isBlocked ? theme.color.dangerText : theme.color.muted,
                        }}>{Number(k.slice(8))}</span>
                        {isBlocked && (
                          <span title={holiday ? `Firm holiday${holidayMap.get(k) ? ` — ${holidayMap.get(k)}` : ""}` : conflict ? "Blocked, but you still have bookings on this day" : "You are unavailable"}
                            style={{ fontSize: 9.5, fontWeight: 700, color: theme.color.dangerText, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                            {conflict ? "Clash" : holiday ? "Holiday" : "Off"}
                          </span>
                        )}
                      </div>
                      {shown.map((c) => c.node)}
                      {chips.length > shown.length && <div style={{ fontSize: 10.5, color: theme.color.accentText, fontWeight: 600 }}>+{chips.length - shown.length} more</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === "week" && (
            <TeamWeek days={days} team={team} meId={user.id} today={today} byDay={byDay} selected={selected} onSelectDay={selectDay} holidays={holidayMap} />
          )}

          {view === "agenda" && (
            <Agenda days={days} byDay={byDay} today={today} blockedSet={blockedSet} onSelectDay={selectDay} onOpenClient={onSelect} />
          )}

          <Legend />
        </div>

        {selected && (
          <DayPanel
            key={selected}
            k={selected}
            today={today}
            items={byDay[selected] ?? { events: [], slots: [], pending: [] }}
            blocked={blockedSet.has(selected)}
            reason={blockedReason.get(selected) ?? null}
            holiday={holidayMap.has(selected) ? { reason: holidayMap.get(selected) ?? null } : null}
            weekend={isWeekendKey(selected)}
            isFirmAdmin={isFirmAdmin}
            onToggleHoliday={(reason) => toggleHoliday(selected, reason)}
            myBooked={myBooked(selected)}
            team={others}
            busy={busy}
            error={actionError}
            onClose={() => selectDay(null)}
            onToggleBlock={() => toggleBlock(selected)}
            onAnswer={answer}
            onResolve={() => setResolving(selected)}
            onOpenClient={onSelect}
          />
        )}
      </div>

      {resolving && (
        <ConflictModal
          date={resolving}
          slots={slots.filter((s) => s.slotDate.slice(0, 10) === resolving)}
          pending={pending.filter((s) => s.slotDate.slice(0, 10) === resolving)}
          team={others}
          today={today}
          onClose={() => { setResolving(null); refresh(); }}
        />
      )}
      {holidayConflicts && (
        <ConflictModal
          date={holidayConflicts.date}
          title="Bookings on a new firm holiday"
          intro="These bookings fall on the day you have just made a firm holiday. Nobody can be booked that day, so move each one to another weekday."
          slots={holidayConflicts.slots.filter((s) => s.status === "confirmed")}
          pending={holidayConflicts.slots.filter((s) => s.status !== "confirmed")}
          team={[]}
          today={today}
          onClose={() => { setHolidayConflicts(null); refresh(); }}
        />
      )}
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────

function PendingRow({ s, busy, blockedThatDay, past, onAnswer, onOpenDay }: {
  s: SlotEntry; busy: boolean; blockedThatDay: boolean; past: boolean;
  onAnswer: (a: "confirmed" | "declined") => void; onOpenDay: () => void;
}) {
  const k = s.slotDate.slice(0, 10);
  return (
    <div data-deeplink={s.id} style={{ padding: "8px 12px", background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>
          {s.organizationName} · <button onClick={onOpenDay} style={{ background: "none", border: "none", padding: 0, color: theme.color.accentText, cursor: "pointer", fontSize: 12.5, fontWeight: 600, textDecoration: "underline" }}>{fromKey(k).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</button>
          {" · "}{hrs(s.hours)} {s.grade}
        </div>
        {s.note && <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 2 }}>{s.note}</div>}
        {blockedThatDay && <div style={{ fontSize: 11.5, color: theme.color.dangerText, marginTop: 2 }}>You have blocked this day — unblock it or decline.</div>}
        {past && <div style={{ fontSize: 11.5, color: theme.color.dangerText, marginTop: 2 }}>This date has passed — decline it.</div>}
      </div>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: theme.color.accentText }}>£{(s.costPence / 100).toLocaleString("en-GB", { maximumFractionDigits: 0 })}</span>
      <div style={{ display: "flex", gap: 6 }}>
        <button disabled={busy || blockedThatDay || past} onClick={() => onAnswer("confirmed")} style={{ ...btnPrimary, padding: "5px 12px", fontSize: 12, opacity: busy || blockedThatDay || past ? 0.5 : 1 }}>{busy ? "…" : "Confirm"}</button>
        <button disabled={busy} onClick={() => onAnswer("declined")} style={{ ...btnSecondary, padding: "5px 12px", fontSize: 12 }}>Decline</button>
      </div>
    </div>
  );
}

function Legend() {
  const sw = (s: React.CSSProperties) => <span style={{ display: "inline-block", width: 12, height: 10, borderRadius: 2, marginRight: 5, verticalAlign: "middle", ...s }} />;
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 11.5, color: theme.color.subtle }}>
      {(["review", "audit", "renewal", "meeting", "booking"] as const).map((k) => (
        <span key={k}>{sw({ background: KIND_STYLE[k].bg, border: `1px solid ${KIND_STYLE[k].color}` })}{k === "booking" ? "Your booking" : KIND_STYLE[k].label}</span>
      ))}
      <span>{sw({ border: `1px dashed ${KIND_STYLE.booking.color}` })}Request awaiting you</span>
      <span>{sw({ borderLeft: `3px solid ${theme.color.danger}`, background: theme.color.panelAlt })}Overdue</span>
      <span>{sw({ background: `repeating-linear-gradient(135deg, ${theme.color.dangerBg}, ${theme.color.dangerBg} 3px, transparent 3px, transparent 6px)`, border: `1px solid ${theme.color.border}` })}You are unavailable, or a firm holiday</span>
      <span>{sw({ background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` })}Weekend — not bookable</span>
      <span style={{ color: theme.color.faint }}>Click a day to open it, block it or answer requests.</span>
    </div>
  );
}

function TeamWeek({ days, team, meId, today, byDay, selected, onSelectDay, holidays }: {
  days: string[]; team: TeamConsultant[]; meId: string; today: string; byDay: Record<string, DayItems>; selected: string | null; onSelectDay: (k: string) => void;
  holidays: Map<string, string | null>;
}) {
  // Me first, then the rest as the API ordered them (grade, name).
  const rows = [...team.filter((c) => c.id === meId), ...team.filter((c) => c.id !== meId)];
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 640 }}>
        <thead>
          <tr>
            <th style={{ width: 150, textAlign: "left", padding: "8px 12px", fontSize: 11, color: theme.color.faint, borderBottom: `1px solid ${theme.color.line}` }}>Consultant</th>
            {days.map((k) => (
              <th key={k} onClick={() => onSelectDay(k)} style={{ padding: "8px 4px", fontSize: 11, color: k === today ? theme.color.accentText : theme.color.faint, borderBottom: `1px solid ${theme.color.line}`, cursor: "pointer", background: selected === k ? theme.color.accentBg : isWeekendKey(k) || holidays.has(k) ? theme.color.panelAlt : "transparent" }}>
                {fromKey(k).toLocaleDateString("en-GB", { weekday: "short" })} {Number(k.slice(8))}
                {holidays.has(k) && <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.dangerText }} title={holidays.get(k) ?? undefined}>Firm holiday</div>}
                {((byDay[k]?.events.length ?? 0) > 0) && <div style={{ fontSize: 10, fontWeight: 500, color: theme.color.subtle }}>{byDay[k].events.length} event{byDay[k].events.length !== 1 ? "s" : ""}</div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={8} style={{ padding: 20, fontSize: 13, color: theme.color.subtle, textAlign: "center" }}>No consultants in the firm yet.</td></tr>
          )}
          {rows.map((c) => (
            <tr key={c.id}>
              <td style={{ padding: "8px 12px", fontSize: 12.5, fontWeight: 600, color: theme.color.text, borderBottom: `1px solid ${theme.color.border}`, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.id === meId ? "You" : c.displayName}
                {c.primaryGrade && <div style={{ fontSize: 10.5, fontWeight: 500, color: theme.color.faint, textTransform: "capitalize" }}>{c.primaryGrade}</div>}
              </td>
              {days.map((k) => {
                const weekend = isWeekendKey(k);
                const holiday = holidays.has(k);
                const off = c.blockedDays.includes(k) || holiday;
                const h = c.bookings[k] ?? 0;
                const p = c.pending?.[k] ?? 0;
                const used = h + p;
                const full = used >= DAY_HOURS;
                return (
                  <td key={k} onClick={() => onSelectDay(k)}
                    title={weekend ? "Weekend — not bookable" : off ? `${c.displayName} — ${holiday ? "firm holiday" : "unavailable"}${h ? ` (still ${hrs(h)} booked)` : ""}` : `${c.displayName} — ${h}h booked${p ? `, ${p}h requested` : ""} of ${DAY_HOURS}h`}
                    style={{ padding: "6px 6px", borderBottom: `1px solid ${theme.color.border}`, cursor: "pointer", verticalAlign: "middle", background: weekend ? theme.color.panelAlt : "transparent" }}>
                    {weekend ? (
                      <div style={{ fontSize: 10.5, textAlign: "center", color: theme.color.faint }}>—</div>
                    ) : off ? (
                      <div style={{ fontSize: 10.5, fontWeight: 700, textAlign: "center", color: theme.color.dangerText, background: theme.color.dangerBg, borderRadius: 4, padding: "3px 0" }}>{h > 0 ? "Clash" : holiday ? "Holiday" : "Off"}</div>
                    ) : (
                      <>
                        <div style={{ height: 6, borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, overflow: "hidden", display: "flex" }}>
                          <div style={{ width: `${Math.min(100, (h / DAY_HOURS) * 100)}%`, height: "100%", background: full ? theme.color.warning : theme.color.accent }} />
                          {/* Hours held by requests not yet answered */}
                          <div style={{ width: `${Math.min(100 - Math.min(100, (h / DAY_HOURS) * 100), (p / DAY_HOURS) * 100)}%`, height: "100%", background: `repeating-linear-gradient(135deg, ${theme.color.accent}, ${theme.color.accent} 2px, transparent 2px, transparent 4px)` }} />
                        </div>
                        <div style={{ fontSize: 10.5, color: full ? theme.color.warningText : theme.color.subtle, textAlign: "center", marginTop: 2 }}>
                          {full ? "Full" : `${DAY_HOURS - used}h free`}{p > 0 && !full ? ` · ${p}h asked` : ""}
                        </div>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Agenda({ days, byDay, today, blockedSet, onSelectDay, onOpenClient }: {
  days: string[]; byDay: Record<string, DayItems>; today: string; blockedSet: Set<string>;
  onSelectDay: (k: string) => void; onOpenClient: (id: string, name: string) => void;
}) {
  const withItems = days.filter((k) => {
    const d = byDay[k];
    return (d && (d.events.length || d.slots.length || d.pending.length)) || blockedSet.has(k);
  });
  if (withItems.length === 0) {
    return <div style={{ ...emptyFrame, color: theme.color.subtle, fontSize: 13, padding: 32 }}>Nothing scheduled in these six weeks.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {withItems.map((k) => {
        const d = byDay[k] ?? { events: [], slots: [], pending: [] };
        return (
          <div key={k} style={{ ...card, padding: 14, display: "flex", gap: 14 }}>
            <button onClick={() => onSelectDay(k)} style={{ width: 52, flexShrink: 0, textAlign: "center", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: k === today ? theme.color.accentText : theme.color.heading, lineHeight: 1 }}>{Number(k.slice(8))}</div>
              <div style={{ fontSize: 10.5, color: theme.color.faint }}>{fromKey(k).toLocaleDateString("en-GB", { weekday: "short", month: "short" })}</div>
            </button>
            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
              {blockedSet.has(k) && <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.dangerText }}>You are unavailable</div>}
              {d.pending.map((s) => <AgendaRow key={s.id} kind="booking" dashed title={`Booking request · ${hrs(s.hours)} ${s.grade}`} client={s.organizationName} onClick={() => onSelectDay(k)} />)}
              {d.slots.map((s) => <AgendaRow key={s.id} kind="booking" title={`Consultancy time · ${hrs(s.hours)} ${s.grade}`} client={s.organizationName} onClick={() => onOpenClient(s.organizationId, s.organizationName)} />)}
              {d.events.map((e) => {
                const st = eventState(e, today);
                return <AgendaRow key={e.id} kind={e.kind} title={e.title} client={e.organizationName ?? "Unknown client"} notes={e.notes} state={st}
                  onClick={() => e.organizationName && onOpenClient(e.organizationId, e.organizationName)} />;
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AgendaRow({ kind, title, client, notes, state, dashed, onClick }: {
  kind: string; title: string; client: string; notes?: string | null; state?: "done" | "overdue" | "open"; dashed?: boolean; onClick: () => void;
}) {
  const c = KIND_STYLE[kind] ?? KIND_STYLE.meeting;
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 10, textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontFamily: theme.font }}>
      <span style={{ width: 4, alignSelf: "stretch", borderRadius: 2, background: state === "overdue" ? theme.color.danger : c.color, opacity: dashed ? 0.5 : 1 }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: theme.color.heading, textDecoration: state === "done" ? "line-through" : "none", opacity: state === "done" ? 0.6 : 1 }}>{title}</span>
        <span style={{ display: "block", fontSize: 11.5, color: theme.color.subtle }}>{client}{notes ? ` — ${notes}` : ""}</span>
      </span>
      <span style={{ fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 3, background: state === "overdue" ? theme.color.dangerBg : c.bg, color: state === "overdue" ? theme.color.dangerText : c.color, flexShrink: 0 }}>
        {state === "overdue" ? "Overdue" : state === "done" ? "Done" : dashed ? "Request" : c.label}
      </span>
    </button>
  );
}

function DayPanel({ k, today, items, blocked, reason, holiday, weekend, isFirmAdmin, myBooked, team, busy, error, onClose, onToggleBlock, onToggleHoliday, onAnswer, onResolve, onOpenClient }: {
  k: string; today: string; items: DayItems; blocked: boolean; reason: string | null;
  holiday: { reason: string | null } | null; weekend: boolean; isFirmAdmin: boolean;
  myBooked: number; team: TeamConsultant[];
  busy: string | null; error: string | null;
  onClose: () => void; onToggleBlock: () => void; onToggleHoliday: (reason: string) => void; onAnswer: (id: string, a: "confirmed" | "declined") => void;
  onResolve: () => void; onOpenClient: (id: string, name: string) => void;
}) {
  const past = k < today;
  const closed = weekend || !!holiday;
  const free = blocked || closed ? 0 : Math.max(0, DAY_HOURS - myBooked);
  const [holidayReason, setHolidayReason] = useState("");
  const section = (label: string) => <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", margin: "14px 0 6px" }}>{label}</div>;
  const status = weekend
    ? "Weekend — consultancy time is booked on weekdays only"
    : holiday
      ? `Firm holiday${holiday.reason ? ` — ${holiday.reason}` : ""}: nobody at the firm can be booked`
      : blocked ? `You are unavailable${reason ? ` — ${reason}` : ""}` : `You have ${free}h of ${DAY_HOURS}h free`;
  return (
    <aside style={{ ...card, flex: "0 1 330px", minWidth: 280, padding: 16, position: "sticky", top: 12 }} aria-label={`Details for ${longDate(k)}`}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: theme.color.heading }}>{longDate(k)}</div>
          <div style={{ fontSize: 12, color: blocked || holiday ? theme.color.dangerText : theme.color.subtle, marginTop: 2 }}>{status}</div>
        </div>
        <button onClick={onClose} aria-label="Close day" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: 2 }}><X size={16} /></button>
      </div>

      {/* Your own availability — nothing to block on a weekend or a firm holiday. */}
      {!past && !closed && (
        <button onClick={onToggleBlock} disabled={busy === `block:${k}`}
          style={{ ...btnSecondary, marginTop: 10, padding: "6px 12px", fontSize: 12, width: "100%", opacity: busy === `block:${k}` ? 0.6 : 1 }}>
          {blocked ? "Make this day available again" : "Mark this day unavailable"}
        </button>
      )}
      {/* The whole firm's — firm administrators only (the API enforces firm:admin). */}
      {isFirmAdmin && !past && !weekend && (
        holiday ? (
          <button onClick={() => onToggleHoliday("")} disabled={busy === `holiday:${k}`}
            style={{ ...btnSecondary, marginTop: 8, padding: "6px 12px", fontSize: 12, width: "100%", opacity: busy === `holiday:${k}` ? 0.6 : 1 }}>
            Remove the firm holiday
          </button>
        ) : (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <input value={holidayReason} onChange={(e) => setHolidayReason(e.target.value)} maxLength={120} placeholder="Reason (optional)" aria-label="Firm holiday reason"
              style={{ flex: 1, minWidth: 0, padding: "5px 8px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: theme.color.panel, color: theme.color.text, fontSize: 12 }} />
            <button onClick={() => { onToggleHoliday(holidayReason); setHolidayReason(""); }} disabled={busy === `holiday:${k}`}
              style={{ ...btnSecondary, padding: "5px 10px", fontSize: 12, whiteSpace: "nowrap", opacity: busy === `holiday:${k}` ? 0.6 : 1 }}>
              Make firm holiday
            </button>
          </div>
        )
      )}
      {(blocked || holiday) && items.slots.length > 0 && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 3, background: theme.color.dangerBg, color: theme.color.dangerText, fontSize: 12 }}>
          You still have {items.slots.length} booking{items.slots.length !== 1 ? "s" : ""} on a day {holiday ? "the firm is closed" : "you blocked"}.
          <button onClick={onResolve} style={{ display: "block", marginTop: 6, background: "none", border: "none", padding: 0, color: theme.color.dangerText, fontWeight: 700, textDecoration: "underline", cursor: "pointer", fontSize: 12 }}>Reassign or reschedule</button>
        </div>
      )}
      {error && <div style={{ marginTop: 10, fontSize: 12, color: theme.color.dangerText }}>{error}</div>}

      {items.pending.length > 0 && (
        <>
          {section("Requests awaiting you")}
          {items.pending.map((s) => (
            <div key={s.id} style={{ padding: 8, border: `1px dashed ${KIND_STYLE.booking.color}`, borderRadius: 3, marginBottom: 6 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>{s.organizationName}</div>
              <div style={{ fontSize: 11.5, color: theme.color.subtle }}>{hrs(s.hours)} {s.grade}{s.note ? ` — ${s.note}` : ""}</div>
              <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                <button disabled={busy === `slot:${s.id}` || blocked || closed || past} onClick={() => onAnswer(s.id, "confirmed")} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 11.5, opacity: blocked || closed || past ? 0.5 : 1 }}>Confirm</button>
                <button disabled={busy === `slot:${s.id}`} onClick={() => onAnswer(s.id, "declined")} style={{ ...btnSecondary, padding: "4px 10px", fontSize: 11.5 }}>Decline</button>
              </div>
            </div>
          ))}
        </>
      )}

      {items.slots.length > 0 && (
        <>
          {section("Your bookings")}
          {items.slots.map((s) => (
            <button key={s.id} onClick={() => onOpenClient(s.organizationId, s.organizationName)} style={{ display: "block", width: "100%", textAlign: "left", padding: 8, borderRadius: 3, border: `1px solid ${theme.color.border}`, background: KIND_STYLE.booking.bg, marginBottom: 6, cursor: "pointer", fontFamily: theme.font }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.text }}>{s.organizationName}</div>
              <div style={{ fontSize: 11.5, color: theme.color.subtle }}>{hrs(s.hours)} {s.grade}{s.note ? ` — ${s.note}` : ""}</div>
            </button>
          ))}
        </>
      )}

      {items.events.length > 0 && (
        <>
          {section("Client events")}
          {items.events.map((e) => {
            const st = eventState(e, today);
            const c = KIND_STYLE[e.kind] ?? KIND_STYLE.meeting;
            return (
              <button key={e.id} onClick={() => e.organizationName && onOpenClient(e.organizationId, e.organizationName)}
                style={{ display: "block", width: "100%", textAlign: "left", padding: 8, borderRadius: 3, border: `1px solid ${theme.color.border}`, borderLeft: `3px solid ${st === "overdue" ? theme.color.danger : c.color}`, background: "transparent", marginBottom: 6, cursor: "pointer", fontFamily: theme.font }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: theme.color.text, textDecoration: st === "done" ? "line-through" : "none" }}>{e.title}</div>
                <div style={{ fontSize: 11.5, color: theme.color.subtle }}>{e.organizationName ?? "Unknown client"} · {c.label}{st === "overdue" ? " · overdue" : st === "done" ? " · done" : ""}</div>
                {e.notes && <div style={{ fontSize: 11.5, color: theme.color.faint, marginTop: 2 }}>{e.notes}</div>}
              </button>
            );
          })}
        </>
      )}

      {items.events.length + items.slots.length + items.pending.length === 0 && (
        <div style={{ fontSize: 12.5, color: theme.color.subtle, marginTop: 12 }}>Nothing scheduled.</div>
      )}

      {team.length > 0 && (
        <>
          {section("Team availability")}
          {team.map((c) => {
            const off = c.blockedDays.includes(k) || closed;
            const h = (c.bookings[k] ?? 0) + (c.pending?.[k] ?? 0);
            return (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", color: theme.color.text }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.displayName}</span>
                <span style={{ color: off ? theme.color.dangerText : h >= DAY_HOURS ? theme.color.warningText : theme.color.subtle, flexShrink: 0, marginLeft: 8 }}>
                  {off ? (closed ? "Closed" : "Unavailable") : h >= DAY_HOURS ? "Full" : `${DAY_HOURS - h}h free`}
                </span>
              </div>
            );
          })}
        </>
      )}
    </aside>
  );
}

// ── Conflict resolution ───────────────────────────────────────────────────
// Bookings on a day the consultant has blocked: each can be handed to a
// colleague who is free that day, or moved to another date. The server
// re-checks grade, availability and capacity (lib/consultantCapacity.ts).

function ConflictModal({ date, slots, pending, team, today, onClose, title = "Bookings on a blocked day", intro }: {
  date: string; slots: SlotEntry[]; pending: SlotEntry[]; team: TeamConsultant[]; today: string; onClose: () => void;
  title?: string; intro?: string;
}) {
  const all = [...slots.map((s) => ({ ...s, status: "confirmed" })), ...pending.map((s) => ({ ...s, status: "pending" }))];
  const [mode, setMode] = useState<Record<string, "reassign" | "reschedule" | "done" | undefined>>({});
  const [target, setTarget] = useState<Record<string, string>>({});
  const [newDate, setNewDate] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Open requests hold their hours too (lib/consultantCapacity.ts), so they count here.
  const free = (c: TeamConsultant, hours: number) => !c.blockedDays.includes(date) && DAY_HOURS - (c.bookings[date] ?? 0) - (c.pending?.[date] ?? 0) >= hours;

  async function submit(id: string, fn: () => Promise<unknown>) {
    setSubmitting((p) => ({ ...p, [id]: true }));
    setErrors((p) => ({ ...p, [id]: "" }));
    try { await fn(); setMode((p) => ({ ...p, [id]: "done" })); }
    catch (e) { setErrors((p) => ({ ...p, [id]: errText(e, "That did not work") })); }
    finally { setSubmitting((p) => ({ ...p, [id]: false })); }
  }

  const inputStyle: React.CSSProperties = { flex: 1, minWidth: 160, padding: "7px 10px", borderRadius: 3, border: `1px solid ${theme.color.borderStrong}`, background: theme.color.panel, color: theme.color.text, fontSize: 13 };

  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 2000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "80px 16px 16px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 24, width: "100%", maxWidth: 560, maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--c-line)" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{title}</div>
            <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 3 }}>
              {longDate(date)} — {intro ?? "reassign or reschedule each booking, or leave it for now. It stays flagged on the calendar until it is moved."}
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, padding: "0 0 0 12px" }}><X size={18} /></button>
        </div>

        {all.length === 0 && <div style={{ fontSize: 13, color: theme.color.subtle }}>No bookings left on this day.</div>}

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {all.map((s) => {
            const m = mode[s.id];
            const busy = !!submitting[s.id];
            const candidates = team.filter((c) => free(c, s.hours));
            return (
              <div key={s.id} style={{ padding: 14, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}`, borderRadius: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.text }}>{s.organizationName}</span>
                  <span style={{ fontSize: 12, color: theme.color.subtle }}>{hrs(s.hours)} {s.grade}</span>
                  <span style={{ marginLeft: "auto" }}><StatusTag tone={s.status === "confirmed" ? "met" : "partial"}>{s.status}</StatusTag></span>
                </div>

                {m === "done" && <div style={{ fontSize: 12, color: theme.color.successText, fontWeight: 600, marginTop: 8 }}>Done</div>}

                {m === "reassign" && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 6 }}>
                      {candidates.length ? "Hand it to a colleague who is free that day:" : "Nobody else has room on this day — reschedule it instead."}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <select value={target[s.id] ?? ""} onChange={(e) => setTarget((p) => ({ ...p, [s.id]: e.target.value }))} style={inputStyle}>
                        <option value="">Select consultant…</option>
                        {candidates.map((c) => (
                          <option key={c.id} value={c.id}>{c.displayName}{c.primaryGrade ? ` (${c.primaryGrade})` : ""} — {DAY_HOURS - (c.bookings[date] ?? 0)}h free</option>
                        ))}
                      </select>
                      <button disabled={!target[s.id] || busy} onClick={() => submit(s.id, () => api.post(`/consultant/slots/${s.id}/reassign`, { toConsultantId: target[s.id] }))}
                        style={{ ...btnPrimary, padding: "7px 14px", opacity: !target[s.id] || busy ? 0.6 : 1 }}>{busy ? "…" : "Reassign"}</button>
                      <button onClick={() => setMode((p) => ({ ...p, [s.id]: undefined }))} style={{ ...btnSecondary, padding: "7px 10px" }}>Back</button>
                    </div>
                  </div>
                )}

                {m === "reschedule" && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 6 }}>Move it to another weekday (the client's Outlook entry moves too):</div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <input type="date" min={today} value={newDate[s.id] ?? ""} onChange={(e) => setNewDate((p) => ({ ...p, [s.id]: e.target.value }))} style={inputStyle} />
                      <button disabled={!newDate[s.id] || busy} onClick={() => submit(s.id, () => api.post(`/consultant/slots/${s.id}/reschedule`, { newDate: newDate[s.id] }))}
                        style={{ ...btnPrimary, padding: "7px 14px", opacity: !newDate[s.id] || busy ? 0.6 : 1 }}>{busy ? "…" : "Move"}</button>
                      <button onClick={() => setMode((p) => ({ ...p, [s.id]: undefined }))} style={{ ...btnSecondary, padding: "7px 10px" }}>Back</button>
                    </div>
                  </div>
                )}

                {!m && (
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={() => setMode((p) => ({ ...p, [s.id]: "reassign" }))} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>Reassign</button>
                    <button onClick={() => setMode((p) => ({ ...p, [s.id]: "reschedule" }))} style={{ ...btnSecondary, padding: "6px 14px", fontSize: 12 }}>Reschedule</button>
                  </div>
                )}
                {errors[s.id] && <div style={{ fontSize: 12, color: theme.color.dangerText, marginTop: 6 }}>{errors[s.id]}</div>}
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
          <button onClick={onClose} style={btnSecondary}>Done for now</button>
        </div>
      </div>
    </div>
  );
}
